import type { Components, Theme } from '@mui/material/styles'

/**
 * ## Why this is `text.primary` and NOT `primary.contrastText`
 *
 * `contrastText` is the colour that is legible ON the primary colour — i.e. on a
 * primary-FILLED surface. A checkbox is not on one. `primary.main` defaults to
 * BLACK in this app (`./palette.ts`), so MUI computed `contrastText` as WHITE and
 * every unstyled checkbox in the app painted white on the ordinary white cards,
 * drawers and sheets it actually sits on.
 *
 * That is not a near-miss, it is a 1.000:1 contrast ratio — measured live on the
 * public certificate access gate, where the location-consent checkbox rendered at
 * full size, `visibility: visible`, checked by default, and could not be seen at
 * all. A visitor was asked to consent to location sharing by a control that was
 * invisible, and it was ON by default.
 *
 * Two call sites had already patched around this locally and independently — see
 * `PhoneVerificationDrawer` ("the app's MUI theme leaves MuiCheckbox with an
 * inherited white fill... invisible on the drawer's white background") and
 * `CreateAccountEmail`. A default that every legible call site has to override is
 * the wrong default; fixing it at the base is what §47 asks for.
 *
 * ## Why `text.primary` specifically, and the same value when checked
 *
 * `text.primary` is the ONE palette entry in this app carrying a legibility
 * contract: it is `theme.text_color || '#000000'`, the colour the tenant
 * designated as readable body text on their own background. `primary.main` is
 * tenant-settable to any colour with no such guarantee — using it here would
 * reintroduce exactly the class of bug being fixed, just less often. It is also
 * the value `ReportMissingDrawer` arrived at independently.
 *
 * Checked takes the same colour rather than an accent, which matches how the
 * app's own checked checkboxes already render: `SelectCheckbox` and the three
 * picker call sites all paint a near-black filled box with a white tick.
 *
 * NOT addressed here (deliberate): `SelectCheckbox` gives its RESTING outline a
 * lighter zinc-400 on the argument that an unchecked box is an invitation, not a
 * value. That is a nicer two-tone treatment, but it is a design change rather
 * than this legibility fix, so the base stays one colour.
 */
export const MuiCheckbox: Components<Theme>['MuiCheckbox'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      padding: 0,
      color: theme.palette.text.primary,

      '&.Mui-checked': {
        color: theme.palette.text.primary,
      },

      '&.MuiCheckbox-indeterminate': {
        color: theme.palette.text.primary,
      },

      '&.Mui-disabled': {
        opacity: 0.3,
        '+ .MuiTypography-root': {
          opacity: 0.3,
        },
      },
    }),
  },
}
